import { useCallback, useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';

/** Status der QR-Scan-Erkennung. */
export type QrScannerStatus = 'idle' | 'scanning' | 'cooldown' | 'error';

/** Optionen fuer useQrScanner. */
export interface UseQrScannerOptions {
  /** Ref auf das bereits laufende Video-Element (aus useCamera). */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Wird aufgerufen sobald ein QR-Code erkannt wurde. Erhaelt den Rohwert des Codes. */
  onScan: (value: string) => void;
  /** Ob der Scanner aktiv scannen soll. Wenn false wird gestoppt. */
  isActive: boolean;
  /** Wartezeit in ms nach einem erfolgreichen Scan, bevor der naechste akzeptiert wird. Default: 2000. */
  cooldownMs?: number;
}

/** Rueckgabewert von useQrScanner. */
export interface UseQrScannerReturn {
  /** Aktueller Status der QR-Erkennung. */
  scannerStatus: QrScannerStatus;
  /** Fehlermeldung im Fehlerfall. */
  scannerError: string | null;
  /** Letzter erkannter QR-Code-Wert (fuer visuelles Feedback). */
  lastScannedValue: string | null;
}

/**
 * Hook fuer QR-Code-Erkennung auf einem laufenden Video-Stream.
 * Setzt `qr-scanner` (Nimiq) auf dem von `useCamera` bereitgestellten Video-Element auf.
 * Enthaelt einen Cooldown-Mechanismus um Mehrfach-Erkennungen desselben Codes zu verhindern.
 *
 * @param options - Konfigurationsoptionen
 * @returns Status und letzter erkannter Wert
 *
 * @example
 * ```tsx
 * const { videoRef } = useCamera();
 * const { scannerStatus, lastScannedValue } = useQrScanner({
 *   videoRef,
 *   onScan: (value) => console.log(value),
 *   isActive: true,
 * });
 * ```
 */
export function useQrScanner({
  videoRef,
  onScan,
  isActive,
  cooldownMs = 2000,
}: UseQrScannerOptions): UseQrScannerReturn {
  const scannerRef = useRef<QrScanner | null>(null);
  const cooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInCooldownRef = useRef(false);

  const [scannerStatus, setScannerStatus] = useState<QrScannerStatus>('idle');
  const [scannerError, setScannerError] = useState<string | null>(null);
  const [lastScannedValue, setLastScannedValue] = useState<string | null>(null);

  const handleDecode = useCallback(
    (result: QrScanner.ScanResult) => {
      if (isInCooldownRef.current) return;

      const value = result.data.trim();
      if (!value) return;

      isInCooldownRef.current = true;
      setLastScannedValue(value);
      setScannerStatus('cooldown');
      onScan(value);

      cooldownRef.current = setTimeout(() => {
        isInCooldownRef.current = false;
        setScannerStatus('scanning');
        setLastScannedValue(null);
      }, cooldownMs);
    },
    [onScan, cooldownMs],
  );

  useEffect(() => {
    if (!isActive) {
      // Scanner stoppen und aufraeumen
      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
        scannerRef.current = null;
      }
      if (cooldownRef.current) {
        clearTimeout(cooldownRef.current);
        cooldownRef.current = null;
      }
      isInCooldownRef.current = false;
      setScannerStatus('idle');
      setScannerError(null);
      setLastScannedValue(null);
      return;
    }

    const videoEl = videoRef.current;
    if (!videoEl) return;

    // Scanner initialisieren
    // Warten bis Video tatsaechlich Daten hat (readyState >= 2 = HAVE_CURRENT_DATA)
    const initScanner = () => {
      if (scannerRef.current) return; // Bereits initialisiert

      setScannerError(null);

      const scanner = new QrScanner(
        videoEl,
        handleDecode,
        {
          // Scanner zeichnet keinen eigenen Overlay — wir haben bereits einen in QrScannerView
          highlightScanRegion: false,
          highlightCodeOutline: false,
          // Kamera wird von useCamera verwaltet, nicht von qr-scanner
          // Daher kein preferredCamera hier
        },
      );

      scanner.start().then(() => {
        scannerRef.current = scanner;
        setScannerStatus('scanning');
      }).catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'QR-Scanner konnte nicht gestartet werden.';
        setScannerError(message);
        setScannerStatus('error');
      });
    };

    // Video ist moeglicherweise noch nicht bereit wenn isActive wechselt
    if (videoEl.readyState >= 2) {
      initScanner();
    } else {
      videoEl.addEventListener('loadeddata', initScanner, { once: true });
    }

    return () => {
      videoEl.removeEventListener('loadeddata', initScanner);
      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
        scannerRef.current = null;
      }
      if (cooldownRef.current) {
        clearTimeout(cooldownRef.current);
        cooldownRef.current = null;
      }
      isInCooldownRef.current = false;
    };
  }, [isActive, videoRef, handleDecode]);

  return { scannerStatus, scannerError, lastScannedValue };
}
